import { isSafeMoneyMinor, isValidMinorUnitExponent } from "./money";
import type { Quotation } from "./model";
import { calculateQuotationTotals } from "./totals";

export type QuotationValidationCode =
  | "SELLER_REQUIRED"
  | "CUSTOMER_REQUIRED"
  | "ISSUE_DATE_INVALID"
  | "VALID_UNTIL_INVALID"
  | "DATE_ORDER_INVALID"
  | "CURRENCY_INVALID"
  | "LINE_ITEMS_REQUIRED"
  | "LINE_DESCRIPTION_REQUIRED"
  | "LINE_QUANTITY_INVALID"
  | "LINE_PRICE_INVALID"
  | "UNKNOWN_INCLUDED_VALUE"
  | "PRICING_REVIEW_REQUIRED"
  | "ADJUSTMENT_INVALID"
  | "TOTAL_INVALID"
  | "QUOTATION_NUMBER_MISSING"
  | "TERMS_MISSING";

export interface QuotationValidationMessage {
  code: QuotationValidationCode;
  severity: "error" | "warning";
  message: string;
  field: string;
}

export interface QuotationValidationResult {
  messages: QuotationValidationMessage[];
  errors: QuotationValidationMessage[];
  warnings: QuotationValidationMessage[];
  canExport: boolean;
}

function isDateOnly(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }

  const [yearText, monthText, dayText] = value.split("-");
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const candidate = new Date(Date.UTC(year, month - 1, day));

  return (
    candidate.getUTCFullYear() === year &&
    candidate.getUTCMonth() === month - 1 &&
    candidate.getUTCDate() === day
  );
}

function message(
  code: QuotationValidationCode,
  severity: "error" | "warning",
  text: string,
  field: string,
): QuotationValidationMessage {
  return { code, severity, message: text, field };
}

export function validateQuotation(quotation: Quotation): QuotationValidationResult {
  const messages: QuotationValidationMessage[] = [];

  if (quotation.seller.name.trim().length === 0) {
    messages.push(
      message(
        "SELLER_REQUIRED",
        "error",
        "Add the seller or business name.",
        "seller-name",
      ),
    );
  }

  if (quotation.customer.name.trim().length === 0) {
    messages.push(
      message(
        "CUSTOMER_REQUIRED",
        "error",
        "Add the customer or recipient name.",
        "customer-name",
      ),
    );
  }

  const issueDateValid = isDateOnly(quotation.issueDate);
  const validUntilValid = isDateOnly(quotation.validUntil);

  if (!issueDateValid) {
    messages.push(
      message("ISSUE_DATE_INVALID", "error", "Enter a valid issue date.", "issue-date"),
    );
  }

  if (!validUntilValid) {
    messages.push(
      message(
        "VALID_UNTIL_INVALID",
        "error",
        "Enter a valid validity date.",
        "valid-until",
      ),
    );
  }

  if (issueDateValid && validUntilValid && quotation.validUntil < quotation.issueDate) {
    messages.push(
      message(
        "DATE_ORDER_INVALID",
        "error",
        "The validity date cannot be before the issue date.",
        "valid-until",
      ),
    );
  }

  if (
    !/^[A-Z]{3}$/.test(quotation.currency) ||
    !isValidMinorUnitExponent(quotation.currencyMinorUnits)
  ) {
    messages.push(
      message(
        "CURRENCY_INVALID",
        "error",
        "Use a three-letter currency and a supported minor-unit precision.",
        "currency",
      ),
    );
  }

  if (quotation.lineItems.length === 0) {
    messages.push(
      message(
        "LINE_ITEMS_REQUIRED",
        "error",
        "Add at least one quotation item.",
        "line-items",
      ),
    );
  }

  if (
    quotation.templateContext.kind === "smart-attendance" &&
    quotation.templateContext.pricingMode === "needs-review"
  ) {
    messages.push(
      message(
        "PRICING_REVIEW_REQUIRED",
        "error",
        "Smart Attendance automatic pricing is not approved. Explicitly switch to labeled manual pricing before export.",
        "smart-pricing-heading",
      ),
    );
  }

  for (const lineItem of quotation.lineItems) {
    const fieldPrefix = `line-${lineItem.id}`;
    if (lineItem.description.trim().length === 0) {
      messages.push(
        message(
          "LINE_DESCRIPTION_REQUIRED",
          "error",
          "Each item needs a description.",
          fieldPrefix,
        ),
      );
    }
    if (!Number.isSafeInteger(lineItem.quantityMilli) || lineItem.quantityMilli <= 0) {
      messages.push(
        message(
          "LINE_QUANTITY_INVALID",
          "error",
          `Enter a positive quantity for ${lineItem.description || "this item"}.`,
          fieldPrefix,
        ),
      );
    }

    const contributes = !lineItem.optional || lineItem.included;
    if (lineItem.valueStatus === "known") {
      if (
        lineItem.unitPriceMinor === null ||
        !isSafeMoneyMinor(lineItem.unitPriceMinor) ||
        lineItem.unitPriceMinor < 0
      ) {
        messages.push(
          message(
            "LINE_PRICE_INVALID",
            "error",
            `Enter a valid unit price for ${lineItem.description || "this item"}.`,
            fieldPrefix,
          ),
        );
      }
    } else if (contributes) {
      messages.push(
        message(
          "UNKNOWN_INCLUDED_VALUE",
          "error",
          `${lineItem.description || "An included item"} is still TBD and cannot be treated as zero.`,
          fieldPrefix,
        ),
      );
    }
  }

  for (const adjustment of quotation.adjustments) {
    if (
      adjustment.included &&
      (adjustment.valueStatus === "tbd" ||
        adjustment.amountMinor === null ||
        !isSafeMoneyMinor(adjustment.amountMinor) ||
        adjustment.amountMinor < 0)
    ) {
      messages.push(
        message(
          "ADJUSTMENT_INVALID",
          "error",
          `${adjustment.label || "An adjustment"} needs a known, valid amount.`,
          `adjustment-${adjustment.id}`,
        ),
      );
    }
  }

  try {
    const totals = calculateQuotationTotals(quotation);
    if (totals.grandTotalMinor !== null && !isSafeMoneyMinor(totals.grandTotalMinor)) {
      messages.push(
        message(
          "TOTAL_INVALID",
          "error",
          "The quotation total is outside the supported range.",
          "totals",
        ),
      );
    }
  } catch {
    messages.push(
      message(
        "TOTAL_INVALID",
        "error",
        "One or more amounts cannot be calculated safely.",
        "totals",
      ),
    );
  }

  if (quotation.quotationNumber.trim().length === 0) {
    messages.push(
      message(
        "QUOTATION_NUMBER_MISSING",
        "warning",
        "Add a quotation number for easier reference.",
        "quotation-number",
      ),
    );
  }

  if (quotation.terms.every((term) => term.trim().length === 0)) {
    messages.push(
      message(
        "TERMS_MISSING",
        "warning",
        "No terms are included in this quotation.",
        "quotation-terms",
      ),
    );
  }

  const errors = messages.filter(({ severity }) => severity === "error");
  const warnings = messages.filter(({ severity }) => severity === "warning");

  return {
    messages,
    errors,
    warnings,
    canExport: errors.length === 0,
  };
}

export function assertQuotationCanExport(quotation: Quotation): void {
  const validation = validateQuotation(quotation);
  if (!validation.canExport) {
    const summary = validation.errors.map(({ message: text }) => text).join(" ");
    throw new Error(`Quotation is not ready to export. ${summary}`);
  }
}
